import React, { useEffect, useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import { AppDispatch } from "../../../store";
import { AuthLayout } from "../../../components/layout";
import { Form } from "../../../components/Form";
import FormTextInput from "../../../components/Form/FormTextInput";
import FormCheckbox from "../../../components/Form/FormCheckbox";
import FormFieldErrors from "../../../components/Form/FormFieldErrors";
import {
  useFormContext,
  FormDataType,
  FormFieldValue,
} from "../../../components/Form/FormContext";
import FormDataSaver from "../../../components/FormDataSaver";
import { selectAuthLoading } from "../../../store/selectors/authSelectors";
import {
  completeRegistration as completeRegistrationAction,
  setRegistrationTokenAction,
  validateRegistrationToken,
} from "../../../store/actions/authActions";
import { validatePassword } from "../../../utils/passwordValidation";
import { validateName } from "../../../utils/nameValidation";
import { ROUTES, STORAGE_KEYS } from "../../../constants";
import { useApiErrorHandling } from "../../../hooks";
import { ValidationResult } from "../../../hooks/useFormValidation";
import {
  RegisterPageContainer,
  RegisterFormContainer,
  RegisterTitle,
  RegisterSubtitle,
  RegisterLink,
} from "./RegisterPage.styled";
import RegistrationErrorPage from "../RegistrationErrorPage";

interface RegisterFormProps {
  apiErrors: string[];
  fieldErrors: { [k: string]: string[] };
}

const RegisterForm: React.FC<RegisterFormProps> = ({
  apiErrors,
  fieldErrors,
}) => {
  const { t } = useTranslation();
  const isLoading = useSelector(selectAuthLoading);
  const { validationResult } = useFormContext();

  return (
    <>
      <FormTextInput
        name="firstName"
        label={t("register.firstName")}
        placeholder={t("register.firstNamePlaceholder")}
        required
      />

      <FormTextInput
        name="password"
        label={t("register.password")}
        type="password"
        placeholder={t("register.passwordPlaceholder")}
        required
      />

      <FormTextInput
        name="confirmPassword"
        label={t("register.confirmPassword")}
        type="password"
        placeholder={t("register.confirmPasswordPlaceholder")}
        required
      />

      <Box sx={{ mt: 5.5 }}>
        <FormCheckbox
          name="agreeToTerms"
          label={
            <Typography component="span">
              {t("register.agreeToTerms")}{" "}
              <Box
                component="a"
                href={ROUTES.TERMS}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e: React.MouseEvent) => e.stopPropagation()}
                sx={{ color: "primary.main" }}
              >
                {t("register.termsOfService")}
              </Box>{" "}
              {t("register.and")}{" "}
              <Box
                component="a"
                href={ROUTES.PRIVACY}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e: React.MouseEvent) => e.stopPropagation()}
                sx={{ color: "primary.main" }}
              >
                {t("register.privacyPolicy")}
              </Box>
            </Typography>
          }
          required
        />
      </Box>

      <FormFieldErrors apiErrors={apiErrors} fieldErrors={fieldErrors} />

      <Box sx={{ mt: 5.5 }}>
        <Button
          type="submit"
          variant="contained"
          fullWidth
          disabled={isLoading || !validationResult.isValid}
        >
          {isLoading
            ? t("register.registeringButton")
            : t("register.registerButton")}
        </Button>
      </Box>
    </>
  );
};

const RegisterPage: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { token } = useParams<{ token: string }>();
  const [isValidatingToken, setIsValidatingToken] = useState(true);
  const { apiErrors, fieldErrors, clearErrors, handleApiError } =
    useApiErrorHandling({
      defaultErrorMessage: "Registration failed. Please try again.",
    });
  const [isTokenValid, setIsTokenValid] = useState(false);
  const [savedFormData, setSavedFormData] = useState<FormDataType | null>(null);

  // Load saved form data on component mount
  useEffect(() => {
    const savedData = sessionStorage.getItem(
      STORAGE_KEYS.REGISTRATION_FORM_DATA
    );
    if (savedData) {
      try {
        setSavedFormData(JSON.parse(savedData));
      } catch (error) {
        console.error("Failed to parse saved form data:", error);
      }
    }
  }, []);

  // Validate token on component mount
  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setIsTokenValid(false);
        setIsValidatingToken(false);
        return;
      }

      try {
        // Store token in Redux
        dispatch(setRegistrationTokenAction(token));

        // Validate token with API
        const result = await dispatch(
          validateRegistrationToken(token)
        ).unwrap();
        setIsTokenValid(result);
      } catch (error) {
        setIsTokenValid(false);
      } finally {
        setIsValidatingToken(false);
      }
    };

    validateToken();
  }, [token, dispatch]);

  const validationRules = {
    firstName: {
      custom: (value: FormFieldValue) => {
        const trimmedValue = String(value || "").trim();
        if (!trimmedValue) {
          return t("register.validation.firstNameRequired");
        }
        const error = validateName(trimmedValue, t);
        return error ? t("register.validation.firstNameInvalid") : null;
      },
    },
    password: {
      custom: (value: FormFieldValue) => {
        const strValue = String(value || "");
        if (!strValue) {
          return t("register.validation.passwordRequired");
        }
        const error = validatePassword(strValue, t);
        return error ? t("register.validation.passwordMinLength") : null;
      },
    },
    confirmPassword: {
      custom: (value: FormFieldValue, formData?: FormDataType) => {
        const strValue = String(value || "");
        if (!strValue) {
          return t("register.validation.confirmPasswordRequired");
        }
        if (strValue && formData?.password && strValue !== formData.password) {
          return t("register.validation.passwordsDoNotMatch");
        }
        return null;
      },
    },
    agreeToTerms: {
      custom: (value: FormFieldValue) => {
        if (!value) {
          return t("register.validation.agreeToTermsRequired");
        }
        return null;
      },
    },
  };

  const handleSubmit = async (
    formData: FormDataType,
    validationResult: ValidationResult
  ) => {
    // Trim whitespace from firstName
    const trimmedFirstName = String(formData.firstName || "").trim();

    // Direct validation check - if any field is invalid, skip request
    const nameError = validateName(trimmedFirstName, t);
    const passwordError = validatePassword(String(formData.password), t);
    const passwordsMatch = formData.password === formData.confirmPassword;
    const termsAccepted = formData.agreeToTerms;

    if (nameError || passwordError || !passwordsMatch || !termsAccepted) {
      return;
    }

    // Additional validation result check
    if (
      !validationResult.isValid ||
      validationResult.errorMessages.length > 0
    ) {
      console.log("❌ VALIDATION FAILED - SKIPPING REQUEST");
      return;
    }

    clearErrors(); // Clear previous errors
    try {
      const registrationData = {
        validationToken: token!,
        password: String(formData.password),
        passwordConfirm: String(formData.confirmPassword),
        name: trimmedFirstName, // Use trimmed name
        consent: Boolean(formData.agreeToTerms),
      };

      await dispatch(completeRegistrationAction(registrationData)).unwrap();

      // Clear saved form data on successful registration
      sessionStorage.removeItem(STORAGE_KEYS.REGISTRATION_FORM_DATA);

      // Redirect to registration success page
      navigate(ROUTES.REGISTRATION_SUCCESS);
    } catch (errors: unknown) {
      handleApiError(errors);
    }
  };

  // Show loading while validating token
  if (isValidatingToken) {
    return (
      <AuthLayout>
        <RegisterPageContainer>
          <RegisterFormContainer>
            <Typography variant="h4" textAlign="center">
              {t("register.validatingToken")}
            </Typography>
          </RegisterFormContainer>
        </RegisterPageContainer>
      </AuthLayout>
    );
  }

  // Show error page if token is invalid
  if (!isTokenValid) {
    return <RegistrationErrorPage />;
  }

  // Show registration form if token is valid
  return (
    <AuthLayout>
      <RegisterPageContainer>
        <RegisterFormContainer>
          <Box
            sx={{
              textAlign: "center",
              position: "absolute",
              top: "71px",
              right: "50px",
            }}
          >
            <RegisterLink to={ROUTES.LOGIN}>
              {t("register.alreadyHaveAccount")}
            </RegisterLink>
          </Box>
          <RegisterTitle>{t("register.title")}</RegisterTitle>

          <RegisterSubtitle>{t("register.subtitle")}</RegisterSubtitle>

          <Form
            validationRules={validationRules}
            onSubmit={handleSubmit}
            initialData={savedFormData || undefined}
            externalErrors={fieldErrors}
            showFieldErrors={false}
            sx={{ display: "flex", flexDirection: "column", gap: "12px" }}
          >
            <FormDataSaver storageKey={STORAGE_KEYS.REGISTRATION_FORM_DATA} />
            <RegisterForm apiErrors={apiErrors} fieldErrors={fieldErrors} />
          </Form>
        </RegisterFormContainer>
      </RegisterPageContainer>
    </AuthLayout>
  );
};

export default RegisterPage;
